import React, { useState } from 'react';
import './TeraGuide.css';

const GIMMICKS = [
  {
    id: 'mega',
    label: 'Mega Evolution',
    badge: 'Active in Reg M-A',
    badgeClass: 'badge--active',
    description:
      'The primary gimmick of Regulation M-A. Mega Evolution transforms a Pokémon into a more powerful form, typically boosting stats and sometimes changing its typing or ability.',
    mechanics: [
      'Requires the Pokémon to hold its corresponding Mega Stone.',
      'Activates at the start of the turn before other moves resolve.',
      'Stat boosts apply immediately — Mega Pokémon are often faster or tankier than base forms.',
      'Some Mega Evolutions have entirely new Abilities (see below).',
      'Only one Mega Evolution per battle — the Omni Ring is consumed.',
    ],
    highlights: [
      { name: 'Mega Meganium', detail: 'Mega Sol — moves act as if Sunny Day is active permanently.' },
      { name: 'Mega Feraligatr', detail: 'Dragonize — Normal-type moves become Dragon-type with 20% power boost.' },
      { name: 'Mega Chesnaught', detail: 'Bulletproof — immune to ball and bomb moves.' },
      { name: 'Mega Dragonite', detail: 'Multiscale — reduces damage when at full HP.' },
      { name: 'Mega Starmie', detail: 'Huge Power — doubles Attack stat.' },
      { name: 'Mega Excadrill', detail: 'Piercing Drill — contact moves deal 1/4 damage through Protect.' },
      { name: 'Mega Salamence', detail: 'Aerilate — Normal-type moves become Flying-type.' },
      { name: 'Mega Gardevoir', detail: 'Pixilate — Normal-type moves become Fairy-type.' },
    ],
    strategy: [
      'Choose your Mega carefully — it\'s a permanent commitment for the battle.',
      'Mega Evolutions that change Abilities (like Mega Meganium\'s Mega Sol) can reshape entire team strategies.',
      'Consider bringing a Mega that covers your team\'s weaknesses rather than just the strongest available.',
      'Opponent can see your full team of 6 before selecting — they may try to counter your Mega pick.',
    ],
  },
  {
    id: 'tera',
    label: 'Terastallization',
    badge: 'Not in Reg M-A',
    badgeClass: 'badge--inactive',
    description:
      'Terastallization is a Paldea-exclusive mechanic that transforms a Pokémon into a crystalline form, changing its type to its Tera type. It is part of the Omni Ring but is NOT available in the current Regulation M-A format.',
    mechanics: [
      'Changes the Pokémon\'s type to its assigned Tera type for the rest of the battle.',
      'If the Tera type matches an original type, that move gets a 2× STAB bonus.',
      'If the Tera type is different from both original types, moves of that type get 1.5× STAB.',
      'Can be used for both offensive coverage and defensive type removal.',
      'One use per battle via the Omni Ring.',
    ],
    highlights: [
      { name: 'Same-type Tera', detail: 'Terastallizing into one of your original types for a 2× STAB boost. Example: Fire Charizard → Fire Tera for maximum Fire damage.' },
      { name: 'Coverage Tera', detail: 'Terastallizing into a different type for surprise coverage. Example: Garchomp → Steel Tera to hit Fairy-types.' },
      { name: 'Defensive Tera', detail: 'Removing a key weakness. Example: Froslass → Water Tera to drop the Rock weakness.' },
    ],
    strategy: [
      'Time Terastallization carefully — it\'s a one-time resource.',
      'Unexpected Tera types are a powerful mind-game tool.',
      'Plan your moveset around your intended Tera type during team building.',
      'Will become relevant when Terastallization-enabled regulations launch in Champions.',
    ],
  },
  {
    id: 'zmoves',
    label: 'Z-Moves',
    badge: 'Not in Reg M-A',
    badgeClass: 'badge--inactive',
    description:
      'Z-Moves are one-time-use super-powered moves channeled through a Z-Crystal. Available in Pokémon Champions via the Omni Ring but not active in the current Regulation M-A format.',
    mechanics: [
      'Requires the Pokémon to hold a corresponding Z-Crystal.',
      'Converts a compatible move into a massively powered Z-Move.',
      'Type-specific Z-Moves boost the base move\'s power significantly.',
      'Pokémon-specific Z-Moves have unique effects beyond raw power.',
      'One use per battle via the Omni Ring.',
    ],
    highlights: [
      { name: 'Type Z-Moves', detail: 'Each type has a Z-Move (e.g., Breakneck Blitz for Normal, Gigavolt Havoc for Electric). Power scales with the base move\'s power.' },
      { name: 'Signature Z-Moves', detail: 'Certain Pokémon have unique Z-Moves with special effects, like Pikachu\'s Catastropika or Snorlax\'s Pulverizing Pancake.' },
    ],
    strategy: [
      'Z-Moves ignore accuracy checks and cannot be Protected against (they can be redirected).',
      'Best used on a Pokémon with a very high base power move to maximize the Z-Move\'s damage.',
      'Status Z-Moves can have unique utility effects like fully healing HP or raising all stats.',
    ],
  },
  {
    id: 'dynamax',
    label: 'Dynamax / Gigantamax',
    badge: 'Not in Reg M-A',
    badgeClass: 'badge--inactive',
    description:
      'Dynamax enlarges a Pokémon and greatly boosts its HP for 3 turns. Gigantamax is a special form of Dynamax available to specific Pokémon, giving them a unique G-Max Move. Available in Pokémon Champions but not active in the current Regulation M-A format.',
    mechanics: [
      'Triples max HP for 3 turns, then the Pokémon reverts.',
      'Regular moves become Max Moves, which have secondary field effects (e.g., Max Airstream raises Speed).',
      'Gigantamax Pokémon get a unique G-Max Move with a special effect instead of the generic Max Move.',
      'Max Moves cannot be blocked by standard Protect — only Max Guard.',
      'One use per battle via the Omni Ring.',
    ],
    highlights: [
      { name: 'Max Airstream', detail: 'Flying-type Max Move — raises Speed of all allies by one stage.' },
      { name: 'Max Quake', detail: 'Ground-type Max Move — raises Sp. Def of all allies by one stage.' },
      { name: 'G-Max Wildfire', detail: 'Gigantamax Charizard — inflicts residual Fire damage on non-Fire-types for 4 turns.' },
    ],
    strategy: [
      'In Doubles, Max Moves that buff allies (Airstream, Steelspike) can shift the tempo of the entire match.',
      'Gigantamax forms are often preferred over standard Dynamax for their unique G-Max effects.',
      'The 3-turn window creates a strong momentum push — time it when you have the advantage.',
    ],
  },
];

const TeraGuide = () => {
  const [activeGimmick, setActiveGimmick] = useState('mega');
  const current = GIMMICKS.find(g => g.id === activeGimmick);

  return (
    <div className="tera-guide">
      <div className="tera-guide__header">
        <h1>Battle Gimmicks Guide</h1>
        <p className="tera-guide__intro">
          In Pokémon Champions, all battle gimmicks are accessed through the{' '}
          <strong>Omni Ring</strong>. You can only activate <strong>one gimmick per battle</strong>,
          so choosing the right one for your team is a core strategic decision.
          The current format is <strong>Regulation M-A</strong> — Mega Evolution only.
        </p>
        <div className="omni-ring-note">
          <strong>Omni Ring Rule:</strong> Mega Evolution, Terastallization, Z-Moves, and Dynamax
          are all routed through a single device. Pick one gimmick per battle — you cannot use more than one.
        </div>
      </div>

      <div className="tera-guide__content">
        <div className="gimmick-tabs">
          {GIMMICKS.map(g => (
            <button
              key={g.id}
              className={`gimmick-tab${activeGimmick === g.id ? ' gimmick-tab--active' : ''}`}
              onClick={() => setActiveGimmick(g.id)}
            >
              {g.label}
              <span className={`gimmick-badge ${g.badgeClass}`}>{g.badge}</span>
            </button>
          ))}
        </div>

        <div className="gimmick-detail">
          <div className="gimmick-detail__header">
            <h2>{current.label}</h2>
            <span className={`gimmick-badge gimmick-badge--large ${current.badgeClass}`}>{current.badge}</span>
          </div>

          <p className="gimmick-detail__description">{current.description}</p>

          <section className="tera-guide__section">
            <h3>How It Works</h3>
            <ul className="tera-guide__features">
              {current.mechanics.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </section>

          <section className="tera-guide__section">
            <h3>{current.id === 'mega' ? 'Notable Examples (Reg M-A)' : 'Notable Examples'}</h3>
            <div className="tera-guide__examples">
              {current.highlights.map((h, i) => (
                <div key={i} className="example-card">
                  <h4>{h.name}</h4>
                  <p>{h.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="tera-guide__section">
            <h3>Strategy Tips</h3>
            <ul className="tera-guide__tips">
              {current.strategy.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TeraGuide;
